import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {ChevronDown, LayoutGrid, Check} from 'lucide-react';
import {OFFER_CATEGORIES, OFFER_STATUSES} from '@chekin/core';
import {useSummary} from 'context/summary';
import {DEVICE, useModalControls, useScreenResize} from '@chekin/guestapp-ui-kit';
import ModalSheet from 'components/common/ModalSheet';
import {getCategoryIcon} from '../OfferCard/constants';
import {
  FilterWrapper,
  FilterTrigger,
  TriggerContent,
  OptionItem,
  CheckIconWrapper,
  OptionsContainer,
  DropdownContainer,
  Overlay,
} from './styled';

type CategoryValue = (typeof OFFER_CATEGORIES)[keyof typeof OFFER_CATEGORIES] | null;

type Props = {
  selectedCategory: CategoryValue;
  onCategoryChange: (category: CategoryValue) => void;
};

const CategoryFilter = ({selectedCategory, onCategoryChange}: Props) => {
  const {t} = useTranslation();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const {data: summary} = useSummary();

  const isGuruWalkActive =
    summary?.guruwalk_status === OFFER_STATUSES.active && !!summary?.location;

  const categories = useMemo(() => {
    const allCategories = Object.values(OFFER_CATEGORIES);
    if (isGuruWalkActive) {
      // Ensure FREE_OFFERS is at the end
      const withoutFreeOffers = allCategories.filter(
        c => c !== OFFER_CATEGORIES.freeOffers,
      );
      return [...withoutFreeOffers, OFFER_CATEGORIES.freeOffers];
    }
    // Hide FREE_OFFERS if GuruWalk is not active
    return allCategories.filter(c => c !== OFFER_CATEGORIES.freeOffers);
  }, [isGuruWalkActive]);

  const getCategoryLabel = (category: CategoryValue): string => {
    if (category === null) {
      return t('all');
    }
    return t(`offer_categories.${category.toLowerCase()}`);
  };

  const handleSelect = (category: CategoryValue) => {
    onCategoryChange(category);
    closeModal();
  };

  const SelectedIcon =
    selectedCategory === null ? LayoutGrid : getCategoryIcon(selectedCategory);

  const optionsContent = (
    <OptionsContainer>
      <OptionItem $active={selectedCategory === null} onClick={() => handleSelect(null)}>
        <LayoutGrid size={20} />
        {getCategoryLabel(null)}
        {selectedCategory === null && (
          <CheckIconWrapper>
            <Check size={20} />
          </CheckIconWrapper>
        )}
      </OptionItem>

      {categories.map(category => {
        const Icon = getCategoryIcon(category);
        const isActive = selectedCategory === category;

        return (
          <OptionItem
            key={category}
            $active={isActive}
            onClick={() => handleSelect(category)}
          >
            <Icon size={20} />
            {getCategoryLabel(category)}
            {isActive && (
              <CheckIconWrapper>
                <Check size={20} />
              </CheckIconWrapper>
            )}
          </OptionItem>
        );
      })}
    </OptionsContainer>
  );

  return (
    <FilterWrapper>
      <FilterTrigger onClick={openModal}>
        <TriggerContent>
          <SelectedIcon size={18} />
          {getCategoryLabel(selectedCategory)}
        </TriggerContent>
        <ChevronDown size={18} />
      </FilterTrigger>

      {isMobileMode ? (
        <ModalSheet
          isOpenModalSheet={isOpen}
          handleCloseModal={closeModal}
          title={t('filter_by_category')}
        >
          {optionsContent}
        </ModalSheet>
      ) : (
        isOpen && (
          <>
            <Overlay onClick={closeModal} />
            <DropdownContainer>{optionsContent}</DropdownContainer>
          </>
        )
      )}
    </FilterWrapper>
  );
};

export {CategoryFilter};
export type {CategoryValue};
